/**
 * How long a vendo has been up, or down.
 *
 * ONE implementation on purpose. The device LIST wants a compact "up 6d 3h" for
 * a table cell; the device CARD wants a stat tile. Two formatters would drift,
 * and "how long has this been down" is exactly the number an operator repeats
 * back to us in a support message — it has to mean the same thing everywhere.
 *
 * WHERE THE ANCHOR COMES FROM — two sources, in order of preference:
 *
 *   1. The box's own connection history (`router.diag.events`). It records every
 *      transition to flash and survives a power cut, so the last "connected"
 *      entry is when this box actually came up. Only boxes on 1.0.24+ have it.
 *
 *   2. `router.lastSeenAt` — when it last spoke to us. For a box that has gone
 *      quiet this is exactly right ("down since then"). For an online box it is
 *      only "a few seconds ago" and says nothing about how long it has been up,
 *      so it is used for DOWN only.
 *
 * A device we cannot anchor shows nothing. "up 0m" would be a confident lie
 * about a box we know nothing about.
 */

/** Compact by design — the list view puts this beside fifteen other columns. */
export const compactDuration = (ms: number): string => {
   const minutes = Math.max(0, Math.floor(ms / 60000));
   if (minutes < 1) return 'just now';
   if (minutes < 60) return `${minutes}m`;
   const hours = Math.floor(minutes / 60);
   if (hours < 24) {
      const rem = minutes % 60;
      return rem ? `${hours}h ${rem}m` : `${hours}h`;
   }
   const days = Math.floor(hours / 24);
   const remH = hours % 24;
   return remH ? `${days}d ${remH}h` : `${days}d`;
};

export interface Uptime {
   /** "6d 3h" — the duration alone, so each view can label it its own way. */
   duration: string;
   /** true = currently offline; render in the warning colour. */
   down: boolean;
   /** "Online since 13/08/2026, 22:35" */
   title: string;
   /** Epoch ms the state began. */
   since: number;
}

const OFFLINE_AFTER_MS = 4 * 60 * 1000;

/**
 * `boot` is derived on the box as now-uptime, both whole seconds, so it wobbles
 * by a second between runs. net-watchdog.sh compares it with this same tolerance
 * before deciding a restart happened; reading it any stricter here would make a
 * perfectly healthy box appear to reboot every minute.
 */
const BOOT_SAME_TOLERANCE_SEC = 30;

/**
 * The last moment the box reported being connected, from its own history.
 *
 * Events look like `<epoch> <tunnel> <inet> <agent> boot=<epoch>`. Walking back
 * from the newest, the anchor is the OLDEST entry in the current unbroken run of
 * "up" — that is when this stretch of uptime began. Stopping at the newest "up"
 * would restart the clock on any unrelated change, e.g. the shop's internet
 * flickering while our tunnel held.
 *
 * The run is broken by a "down" entry OR by the boot epoch changing, because a
 * power cut only ever produces the second of those.
 */

export const upSince = (
   events: string[] | undefined,
   /** `boot=<epoch>` out of `diag.outage`, seconds. The anchor may never
    *  predate it — see the step-16 note in uptime(). */
   bootSec?: number | null
): number | null => {
   if (!Array.isArray(events) || !events.length) return null;
   const parsed = events
      .map((line) => {
         const p = String(line).trim().split(/\s+/);
         const at = Number(p[0]);
         // Field 5 is `boot=<epoch>`, added with the self-report firmware.
         // Older boxes send four fields; undefined must read as "no opinion",
         // never as "the boot epoch changed".
         const b = /^boot=(\d+)$/.exec(p[4] ?? '');
         const boot = b ? Number(b[1]) : undefined;
         return Number.isFinite(at) && at > 0
            ? { at, tunnel: p[1], boot }
            : null;
      })
      .filter(
         (e): e is { at: number; tunnel: string; boot: number | undefined } =>
            e !== null
      )
      .sort((a, b) => a.at - b.at);

   if (!parsed.length) return null;
   const newest = parsed[parsed.length - 1];
   if (newest.tunnel !== 'up') return null; // not up now

   // 🔴 A POWER CUT LEAVES NO "down" ENTRY. The agent dies with the power, so
   // nothing is written on the way out and the run of "up" reads as unbroken —
   // 80 vendos were reporting uptime straight through a real outage on
   // 2026-08-23 (JMN PISOWIFI 6: "up 187h", rebooted 1h earlier).
   //
   // The box does record it, just not as a transition: the boot epoch on the
   // line CHANGES across a restart, and net-watchdog.sh forces a fresh line at
   // that moment even when the tunnel state is identical either side. So a
   // change of boot= breaks the run exactly like a "down" does.
   let anchor = newest.at;
   for (let i = parsed.length - 1; i >= 0; i--) {
      if (parsed[i].tunnel !== 'up') break;
      if (
         newest.boot !== undefined &&
         parsed[i].boot !== undefined &&
         Math.abs((parsed[i].boot as number) - newest.boot) >
            BOOT_SAME_TOLERANCE_SEC
      ) {
         break; // this entry belongs to a previous life of the box
      }
      anchor = parsed[i].at;
   }

   // Backstop for a box whose restart is not visible in the events it still
   // holds — only 8 lines are kept, so the boundary can have rotated away.
   // A clamp may only ever move the anchor FORWARD: an old outage must never
   // stretch a box that came up an hour ago into a week of uptime.
   if (
      bootSec !== undefined &&
      bootSec !== null &&
      Number.isFinite(bootSec) &&
      bootSec > anchor
   ) {
      anchor = bootSec;
   }
   return anchor * 1000;
};

/** Pull `boot=<epoch>` out of the box's frozen post-mortem line, which looks
 *  like `boot=1786326000 dark=18360 cause=power cut, manual reboot or crash`. */
export const outageBootSec = (outage?: string | null): number | null => {
   const m = /boot=(\d+)/.exec(String(outage ?? ''));
   if (!m) return null;
   const n = Number(m[1]);
   return Number.isFinite(n) && n > 0 ? n : null;
};

export function uptime(
   args: {
      /** When the router last spoke to us. */
      lastSeenAt?: string | Date | null;
      /** The box's own connection history, when it reports one. */
      events?: string[];
      /** The box's frozen post-mortem line. Its `boot=` is the only thing that
       *  stops the up-clock counting through a power cut. */
      outage?: string | null;
   },
   now: number = Date.now()
): Uptime | null {
   const seen = args.lastSeenAt ? new Date(args.lastSeenAt).getTime() : NaN;
   const hasSeen = Number.isFinite(seen);
   const isDown = !hasSeen || now - seen > OFFLINE_AFTER_MS;

   if (isDown) {
      // Down: the anchor is unambiguous — it stopped speaking.
      if (!hasSeen) return null;
      return {
         duration: compactDuration(now - seen),
         down: true,
         title: `Offline since ${new Date(seen).toLocaleString()}`,
         since: seen,
      };
   }

   // Up: only the box's own history can say since WHEN. lastSeenAt is always
   // "seconds ago" for a healthy box and would read as "up 0m" forever.
   // Clamped to the last boot the box itself reported. Applied to the UP case
   // only: "offline for" is measured from when it stopped speaking to us, and a
   // boot epoch from before that outage must never shorten it.
   const since = upSince(args.events, outageBootSec(args.outage));
   if (since === null) return null;
   return {
      duration: compactDuration(now - since),
      down: false,
      title: `Online since ${new Date(since).toLocaleString()}`,
      since,
   };
}
